import React, { Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function CheckMark({
  highlighted
}: {
  highlighted?: boolean;
}) {
  return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" className="flex-shrink-0 mt-0.5" data-id="element-126">
      <circle cx="9" cy="9" r="8" fill={highlighted ? '#FF6B6B' : '#1B2A4A'} fillOpacity={highlighted ? '0.2' : '1'} stroke={highlighted ? '#FF6B6B' : '#FF6B6B'} strokeWidth="1.5" strokeOpacity={highlighted ? '1' : '0.4'} data-id="element-127" />
      <path d="M5.5 9l2.5 2.5 4.5-5" stroke={highlighted ? '#FF6B6B' : '#FF8A5C'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-128" />
    </svg>;
}
export function Pricing() {
  return <section id="pricing" className="w-full bg-navy py-20 md:py-28 px-6" data-id="element-129">
      <div className="max-w-6xl mx-auto" data-id="element-130">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-131">
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4" data-id="element-132">
            Choose Your Tier
          </h2>
          <p className="font-body text-slate-400 text-base md:text-lg max-w-xl mx-auto" data-id="element-133">
            Scale your loyalty infrastructure as your player base grows.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5 items-center" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} data-id="element-134">
          {/* Challenger */}
          <motion.div variants={cardVariants} className="bg-navy-light/60 backdrop-blur-sm border border-white/5 rounded-2xl p-7 md:p-8 flex flex-col h-full" data-id="element-135">
            <div className="mb-6" data-id="element-136">
              <h3 className="font-display font-bold text-lg text-white mb-1" data-id="element-137">
                Challenger
              </h3>
              <p className="font-body text-slate-500 text-sm mb-5" data-id="element-138">
                For operators testing the waters of AI-driven loyalty
              </p>
              <div className="flex items-baseline gap-1" data-id="element-139">
                <span className="font-display font-bold text-4xl text-white" data-id="element-140">
                  €2,900
                </span>
                <span className="font-body text-slate-500 text-sm" data-id="element-141">/mo</span>
              </div>
            </div>
            <ul className="space-y-3.5 mb-8 flex-1" role="list" data-id="element-142">
              <li className="flex items-start gap-3" data-id="element-143">
                <CheckMark data-id="element-144" />
                <span className="font-body text-slate-300 text-sm" data-id="element-145">
                  Up to 10K active players
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-146">
                <CheckMark data-id="element-147" />
                <span className="font-body text-slate-300 text-sm" data-id="element-148">
                  3 loyalty tiers
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-149">
                <CheckMark data-id="element-150" />
                <span className="font-body text-slate-300 text-sm" data-id="element-151">
                  Basic churn prediction
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-152">
                <CheckMark data-id="element-153" />
                <span className="font-body text-slate-300 text-sm" data-id="element-154">
                  Email engagement triggers
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-155">
                <CheckMark data-id="element-156" />
                <span className="font-body text-slate-300 text-sm" data-id="element-157">
                  Monthly performance reports
                </span>
              </li>
            </ul>
            <a href="#" className="inline-flex items-center justify-center w-full py-3 border-2 border-coral/50 text-coral font-body font-semibold rounded-full text-sm transition-all duration-300 hover:bg-coral hover:text-white hover:border-coral" data-id="element-158">
              Get Started
            </a>
          </motion.div>

          {/* Contender — highlighted */}
          <motion.div variants={cardVariants} className="relative bg-navy-light/80 backdrop-blur-sm border-2 border-coral rounded-2xl p-7 md:p-9 flex flex-col md:scale-105 md:-my-4 shadow-[0_0_50px_rgba(255,107,107,0.12)] h-full" data-id="element-159">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2" data-id="element-160">
              <span className="inline-block px-4 py-1 bg-gradient-to-r from-coral to-coral-light text-white font-body font-semibold text-xs rounded-full tracking-wide uppercase" data-id="element-161">
                Most Popular
              </span>
            </div>
            <div className="mb-6 mt-2" data-id="element-162">
              <h3 className="font-display font-bold text-lg text-white mb-1" data-id="element-163">
                Contender
              </h3>
              <p className="font-body text-slate-400 text-sm mb-5" data-id="element-164">
                For operators ready to dominate retention
              </p>
              <div className="flex items-baseline gap-1" data-id="element-165">
                <span className="font-display font-bold text-4xl text-coral" data-id="element-166">
                  €7,500
                </span>
                <span className="font-body text-slate-500 text-sm" data-id="element-167">/mo</span>
              </div>
            </div>
            <ul className="space-y-3.5 mb-8 flex-1" role="list" data-id="element-168">
              <li className="flex items-start gap-3" data-id="element-169">
                <CheckMark highlighted data-id="element-170" />
                <span className="font-body text-slate-200 text-sm" data-id="element-171">
                  Up to 100K active players
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-172">
                <CheckMark highlighted data-id="element-173" />
                <span className="font-body text-slate-200 text-sm" data-id="element-174">
                  Unlimited dynamic tiers
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-175">
                <CheckMark highlighted data-id="element-176" />
                <span className="font-body text-slate-200 text-sm" data-id="element-177">
                  Advanced predictive scoring
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-178">
                <CheckMark highlighted data-id="element-179" />
                <span className="font-body text-slate-200 text-sm" data-id="element-180">
                  Multi-channel triggers
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-181">
                <CheckMark highlighted data-id="element-182" />
                <span className="font-body text-slate-200 text-sm" data-id="element-183">
                  Sentiment analysis
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-184">
                <CheckMark highlighted data-id="element-185" />
                <span className="font-body text-slate-200 text-sm" data-id="element-186">
                  Dedicated success manager
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-187">
                <CheckMark highlighted data-id="element-188" />
                <span className="font-body text-slate-200 text-sm" data-id="element-189">
                  Weekly optimization cycles
                </span>
              </li>
            </ul>
            <a href="#" className="inline-flex items-center justify-center w-full py-3 bg-gradient-to-r from-coral to-coral-light text-white font-body font-semibold rounded-full text-sm transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,107,0.4)] hover:scale-[1.02]" data-id="element-190">
              Get Started
            </a>
          </motion.div>

          {/* Apex */}
          <motion.div variants={cardVariants} className="bg-navy-light/60 backdrop-blur-sm border border-white/5 rounded-2xl p-7 md:p-8 flex flex-col h-full" data-id="element-191">
            <div className="mb-6" data-id="element-192">
              <h3 className="font-display font-bold text-lg text-white mb-1" data-id="element-193">
                Apex
              </h3>
              <p className="font-body text-slate-500 text-sm mb-5" data-id="element-194">
                For operators who refuse to lose a single high-value player
              </p>
              <div className="flex items-baseline gap-1" data-id="element-195">
                <span className="font-display font-bold text-4xl text-white" data-id="element-196">
                  €18,000
                </span>
                <span className="font-body text-slate-500 text-sm" data-id="element-197">/mo</span>
              </div>
            </div>
            <ul className="space-y-3.5 mb-8 flex-1" role="list" data-id="element-198">
              <li className="flex items-start gap-3" data-id="element-199">
                <CheckMark data-id="element-200" />
                <span className="font-body text-slate-300 text-sm" data-id="element-201">
                  Unlimited players
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-202">
                <CheckMark data-id="element-203" />
                <span className="font-body text-slate-300 text-sm" data-id="element-204">
                  Full neural reward mapping
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-205">
                <CheckMark data-id="element-206" />
                <span className="font-body text-slate-300 text-sm" data-id="element-207">
                  Real-time VIP command center
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-208">
                <CheckMark data-id="element-209" />
                <span className="font-body text-slate-300 text-sm" data-id="element-210">
                  Custom quest engine design
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-211">
                <CheckMark data-id="element-212" />
                <span className="font-body text-slate-300 text-sm" data-id="element-213">
                  White-glove onboarding
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-214">
                <CheckMark data-id="element-215" />
                <span className="font-body text-slate-300 text-sm" data-id="element-216">
                  24/7 priority support
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-217">
                <CheckMark data-id="element-218" />
                <span className="font-body text-slate-300 text-sm" data-id="element-219">
                  Quarterly strategy sessions
                </span>
              </li>
            </ul>
            <a href="#" className="inline-flex items-center justify-center w-full py-3 border-2 border-coral/50 text-coral font-body font-semibold rounded-full text-sm transition-all duration-300 hover:bg-coral hover:text-white hover:border-coral" data-id="element-220">
              Get Started
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}